import useDiscoveryFetch from './useDiscoveryFetch';
import { useState, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Image, Spinner, Alert } from 'react-bootstrap';
import api from '../../api/axiosInstance';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import { UserContext } from '../../UserContext';
import { isAdminType } from '../../utils/roleUtils';
import CompanyDetailHero from './companyDetail/CompanyDetailHero';
import CompanyDetailMobileTabs from './companyDetail/CompanyDetailMobileTabs';
import CompanyDetailSection from './companyDetail/CompanyDetailSection';
import InfoRow from './companyDetail/InfoRow';
import JobsTabLabel from './companyDetail/JobsTabLabel';
import { useDiscoveryJobsCount } from './companyDetail/useDiscoveryJobsCount';
import { hasAnyValue } from './companyDetail/emptyValue';
import icImgPreview from '../../assets/img/ic-img.svg';
import gptwBanner from '../../assets/img/gptw-banner.svg';
import icApproved from '../../assets/img/ic-check-circle.svg';
import icRejected from '../../assets/img/ic-close-circle.svg';
import icRLifecycleReview from '../../assets/img/ic-lyfecycle-review.svg';
import icUnderReview from '../../assets/img/ic-under-review.svg';
import icLink from '../../assets/img/ic-link-square.svg';
import icMail from '../../assets/img/ic-mail-send.svg';
import icPhone from '../../assets/img/ic-smart-phone.svg';
import icArea from '../../assets/img/ic-city.svg';

const DiscoveryCompanyHiringView = () => {
    const { id, cityId, companyId } = useParams();
    const actualCompanyId = companyId || id;
    const actualCityId = cityId || null;
    const countryCode = companyId ? (id || null) : null;
    const isCountryCityContext = Boolean(companyId && cityId && id);
    const isCountryOnlyContext = Boolean(companyId && !cityId && id);

    const { user } = useContext(UserContext);
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const isAdminViewer = isAdminType(user?.role);
    const { jobsCount } = useDiscoveryJobsCount(actualCompanyId);

    const [company, setCompany] = useState(null);
    const [cityData, setCityData] = useState(null);
    const [countryData, setCountryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchHiringData = async () => {
            try {
                if (!isReconnect || !company) setLoading(true);
                setError('');
                const res = await api.get(`/api/discovery/companies/${actualCompanyId}/hiring`, { signal });
                if (!isCurrent()) return;
                setCompany(res.data);
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching company hiring info:', err);
                setError('An error occurred while fetching company hiring info.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchHiringData();
    }, [actualCompanyId]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!actualCityId) return;
        api.get(`/api/discovery/cities/${actualCityId}`, { signal })
            .then(res => { if (isCurrent()) setCityData(res.data); })
            .catch(() => {});
    }, [actualCityId]);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        if (!isCountryOnlyContext) return;
        api.get(`/api/discovery/countries/${countryCode}`, { signal })
            .then(res => { if (isCurrent()) setCountryData(res.data); })
            .catch(() => {});
    }, [isCountryOnlyContext, countryCode]);

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-company',
        discoveryType: 'companies',
        companyId: actualCompanyId,
        companyName: company?.title || company?.name || '',
        cityName: cityData?.cityName || cityData?.name || company?.cityRegion || '',
        countryName: countryData?.countryName || countryData?.name || '',
        activeTab: 'Hiring',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'companies',
            companyName: company?.title || company?.name || '',
            selectedCity: cityData?.cityName || cityData?.name || company?.cityRegion || '',
            selectedCountry: countryData?.countryName || countryData?.name || company?.country || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!company) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">Company not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    const companyPath = actualCityId
        ? (isCountryCityContext
            ? `/discovery/countries/${countryCode}/cities/${actualCityId}/companies/${actualCompanyId}`
            : `/discovery/cities/${actualCityId}/companies/${actualCompanyId}`)
        : (isCountryOnlyContext
            ? `/discovery/countries/${countryCode}/companies/${actualCompanyId}`
            : `/discovery/companies/${actualCompanyId}`);

    const navItems = [
        { to: companyPath, eventKey: 'companyInfo', label: 'Company Info', end: true },
        { to: `${companyPath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info', end: true },
        { to: `${companyPath}/jobs`, eventKey: 'jobs', label: 'Jobs' },
    ];

    const mobileNavItems = [
        { to: companyPath, eventKey: 'companyInfo', label: 'Company Info', end: true },
        { to: `${companyPath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info', end: true },
        { to: `${companyPath}/jobs`, eventKey: 'jobs', label: <JobsTabLabel jobsCount={jobsCount} /> },
    ];

    const logo = company.logo || '';
    const urlInput = company.url || '';
    const approvalStatus = String(company.accountApprovalStatus || '').toLowerCase();
    const hrRecrutingUrl = company.hrRecrutingUrl || '';

    const cityLabel = company.city
        ? company.city
        : company.address
            ? (() => {
                const parts = company.address.split(',');
                return parts.length >= 2 ? parts[parts.length - 2].trim() : 'N/A';
            })()
            : 'N/A';

    const studyFields = Array.isArray(company.studyFields) ? company.studyFields : [];

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb
                                fromCountry={isCountryCityContext ? countryCode : null}
                                countryName={
                                    isCountryCityContext ? cityData?.country?.name :
                                    isCountryOnlyContext ? (countryData?.countryName || countryData?.name) :
                                    null
                                }
                                cityName={actualCityId ? (cityData?.cityName || cityData?.name) : null}
                                subPage={companyId ? 'Companies' : null}
                                companyName={company?.title || company?.name}
                                label="Hiring Info"
                            />
                        </Col>
                    </Row>

                    {!isMobile ? (
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Card className="card-premium">
                                        <Card.Body>
                                            <Row className="align-items-center3">
                                                <Col>
                                                    <h3 className="text-start">1. General Info</h3>
                                                    <hr />
                                                </Col>
                                            </Row>
                                            <Row className="mb-4">
                                                <Col md={8} className="d-flex">
                                                    <div className="upload-image-wrapper">
                                                        <div className="upload-preview not-editing">
                                                            <Image
                                                                src={logo.trim() ? logo : icImgPreview}
                                                                alt={company.title || 'Company Logo'}
                                                                roundedCircle
                                                                className="preview-image"
                                                                onError={(e) => { e.currentTarget.src = icImgPreview; }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="ms-3">
                                                        <h1>{company.title || '—'}</h1>
                                                        <p>
                                                            {urlInput ? (
                                                                <a
                                                                    href={urlInput.startsWith('http') ? urlInput : `https://${urlInput}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {urlInput}
                                                                </a>
                                                            ) : '—'}
                                                        </p>
                                                        <div className="status-wrapper">
                                                            <p>
                                                                <span className={
                                                                    approvalStatus === 'approved' ? 'approved' :
                                                                    approvalStatus === 'rejected' ? 'rejected' :
                                                                    approvalStatus === 'lifecycle review' ? 'lifecycle-review' :
                                                                    approvalStatus === 'under review' ? 'under-review' : ''
                                                                }>
                                                                    <span className="d-flex align-items-center gap-2">
                                                                        {approvalStatus === 'approved' && <img src={icApproved} alt="Approved" />}
                                                                        {approvalStatus === 'rejected' && <img src={icRejected} alt="Rejected" />}
                                                                        {approvalStatus === 'lifecycle review' && <img src={icRLifecycleReview} alt="Lifecycle Review" />}
                                                                        {approvalStatus === 'under review' && <img src={icUnderReview} alt="Under Review" />}
                                                                        {company.accountApprovalStatus}
                                                                    </span>
                                                                </span>
                                                                <span className={`badge ${company.activityStatus ? 'bg-active' : 'bg-inactive'}`}>
                                                                    {company.activityStatus ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Col>

                                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                                    {parseFloat(company.bestPlacetoWorkRating) > 89 && (() => {
                                                        const rating = parseFloat(company.bestPlacetoWorkRating || 0);
                                                        const redDash = rating;
                                                        const grayDash = 100 - rating;
                                                        return (
                                                            <div className="gptw-badge d-flex align-items-center">
                                                                <div className="gptw-logo">
                                                                    <Image src={gptwBanner} alt="Great Place to Work" />
                                                                </div>
                                                                <div className="gptw-text-circle">
                                                                    <div className="gptw-circle position-relative">
                                                                        <svg width="80" height="80" viewBox="0 0 36 36">
                                                                            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e0e0e0" strokeWidth="3" />
                                                                            <circle
                                                                                cx="18" cy="18" r="15.9155"
                                                                                fill="none" stroke="#e3000b" strokeWidth="3"
                                                                                strokeDasharray={`${redDash} ${grayDash}`}
                                                                                strokeLinecap="round"
                                                                                transform="rotate(-90 18 18)"
                                                                            />
                                                                            <text x="18" y="21" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">
                                                                                {rating}%
                                                                            </text>
                                                                        </svg>
                                                                    </div>
                                                                    <p className="gptw-text">Employees say this is a great place to work</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Row className="mb-4 mt-3">
                                        <Col>
                                            <NavTabs items={navItems} />
                                        </Col>
                                    </Row>

                                    <Row className="mb-5">
                                        <Col><h2>Hiring info</h2></Col>
                                    </Row>

                                    {/* HR section — matches CompanyAddHr non-editing JSX */}
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <Card className="mb-4">
                                                <Card.Body>
                                                    <Row>
                                                        <Col md={12}>
                                                            <h3><span className="item-number">1.</span> HR:&nbsp;<span>{company.hrFullName || ''}</span></h3>
                                                            <hr />
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={4}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icLink} alt="Recruiting portal URL" />
                                                                </span>
                                                                <span className="text">
                                                                    Recruiting portal URL
                                                                    <br />
                                                                    <strong>
                                                                        {hrRecrutingUrl ? (
                                                                            <a
                                                                                href={`https://www.${hrRecrutingUrl}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                {hrRecrutingUrl}
                                                                            </a>
                                                                        ) : (
                                                                            "N/A"
                                                                        )}
                                                                    </strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                        <Col md={4}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icMail} alt="HR email" />
                                                                </span>
                                                                <span className="text">
                                                                    HR email:
                                                                    <br />
                                                                    <strong>
                                                                        {company.hrEmail ? (
                                                                            <a href={`mailto:${company.hrEmail}`}>
                                                                                {company.hrEmail}
                                                                            </a>
                                                                        ) : (
                                                                            "N/A"
                                                                        )}
                                                                    </strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                        <Col md={4}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icPhone} alt="HR phone" />
                                                                </span>
                                                                <span className="text">
                                                                    HR phone:
                                                                    <br />
                                                                    <strong>
                                                                        {company.hrPhone || "N/A"}
                                                                    </strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Job Preferences — matches CompanyAddJobs non-editing JSX */}
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <Card className="mb-4">
                                                <Card.Body>
                                                    <Row>
                                                        <Col md={12}>
                                                            <h3><span className="item-number">2.</span> Job preferences</h3>
                                                            <hr />
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={4}>
                                                            <Card>
                                                                <Card.Header>
                                                                    <div className="d-flex justify-content-between w-100">
                                                                        <span className="fw-bold mb-0">Open for student jobs</span>
                                                                        <span>{company.openedStudent ? 'Yes' : 'No'}</span>
                                                                    </div>
                                                                </Card.Header>
                                                                <Card.Body className="p-3 pt-2">
                                                                    {company.openedStudent && (
                                                                        <>
                                                                            <div className="icon-text mt-2">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icArea} alt="Student job Areas" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Which Areas
                                                                                    <br />
                                                                                    <strong>{cityLabel}</strong>
                                                                                </span>
                                                                            </div>
                                                                            <div className="icon-text mt-3">
                                                                                <span className="text">
                                                                                    Which Corporate Areas
                                                                                    <br />
                                                                                    <strong>
                                                                                        <div className="">
                                                                                            {company.studentJobAreas?.trim()
                                                                                                ? company.studentJobAreas
                                                                                                : 'N/A'
                                                                                            }
                                                                                        </div>
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Card>
                                                                <Card.Header>
                                                                    <div className="d-flex justify-content-between w-100">
                                                                        <span className="fw-bold mb-0">Open for World Traveler</span>
                                                                        <span>{company.openedWorldTraveler ? 'Yes' : 'No'}</span>
                                                                    </div>
                                                                </Card.Header>
                                                                <Card.Body className="p-3 pt-2">
                                                                    {company.openedWorldTraveler && (
                                                                        <>
                                                                            <div className="icon-text mt-2">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icArea} alt="World Traveler Areas" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Which Areas
                                                                                    <br />
                                                                                    <strong>{cityLabel}</strong>
                                                                                </span>
                                                                            </div>
                                                                            <div className="icon-text mt-3">
                                                                                <span className="text">
                                                                                    Which Corporate Areas
                                                                                    <br />
                                                                                    <strong>
                                                                                        <div className="">
                                                                                            {company.worldTravelerAreas?.trim()
                                                                                                ? company.worldTravelerAreas
                                                                                                : 'N/A'
                                                                                            }
                                                                                        </div>
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Card>
                                                                <Card.Header>
                                                                    <div className="d-flex justify-content-between w-100">
                                                                        <span className="fw-bold mb-0">Open for English Speaking Only</span>
                                                                        <span>{company.openedEnglishSpeaking ? 'Yes' : 'No'}</span>
                                                                    </div>
                                                                </Card.Header>
                                                                <Card.Body className="p-3 pt-2">
                                                                    {company.openedEnglishSpeaking && (
                                                                        <>
                                                                            <div className="icon-text mt-2">
                                                                                <span className="icon" aria-hidden="true">
                                                                                    <Image src={icArea} alt="English Speaking Areas" />
                                                                                </span>
                                                                                <span className="text">
                                                                                    Which Areas
                                                                                    <br />
                                                                                    <strong>{cityLabel}</strong>
                                                                                </span>
                                                                            </div>
                                                                            <div className="icon-text mt-3">
                                                                                <span className="text">
                                                                                    Which Corporate Areas
                                                                                    <br />
                                                                                    <strong>
                                                                                        <div className="">
                                                                                            {company.enSpeakingAreas?.trim()
                                                                                                ? company.enSpeakingAreas
                                                                                                : 'N/A'
                                                                                            }
                                                                                        </div>
                                                                                    </strong>
                                                                                </span>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Study Fields */}
                                    {studyFields.length > 0 && (
                                        <Row className="mb-4">
                                            <Col md={12}>
                                                <Card className="mb-4">
                                                    <Card.Body>
                                                        <Row>
                                                            <Col md={12}>
                                                                <h3><span className="item-number">3.</span> Areas & Directions</h3>
                                                                <hr />
                                                            </Col>
                                                        </Row>
                                                        {studyFields.map((sf, idx) => (
                                                            <Row key={idx} className="mb-3">
                                                                {sf.studyField && (
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="text">
                                                                                Study Field
                                                                                <br />
                                                                                <strong>{sf.studyField}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                )}
                                                                {sf.preferredStudyDirections && (
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="text">
                                                                                Preferred Directions
                                                                                <br />
                                                                                <strong>{sf.preferredStudyDirections}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                )}
                                                                {sf.specialistAreas && (
                                                                    <Col md={4}>
                                                                        <div className="icon-text">
                                                                            <span className="text">
                                                                                Specialist Areas
                                                                                <br />
                                                                                <strong>{sf.specialistAreas}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        ))}
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    ) : (
                        <div className="company-detail-mobile">
                            <CompanyDetailHero company={company} isAdminViewer={isAdminViewer} />

                            <CompanyDetailMobileTabs items={mobileNavItems} />

                            <Card className="company-detail-content-card mb-4">
                                <Card.Body>
                                    <h2 className="company-detail-content-title">Hiring info</h2>

                            {hasAnyValue([company.hrFullName, hrRecrutingUrl, company.hrEmail, company.hrPhone]) && (
                                <CompanyDetailSection title="Hiring contacts" number={1}>
                                    <InfoRow label="Name" value={company.hrFullName} />
                                    <InfoRow
                                        icon={icLink}
                                        iconAlt="Recruiting portal URL"
                                        label="Recruiting portal URL"
                                        value={hrRecrutingUrl}
                                        href={hrRecrutingUrl ? `https://www.${hrRecrutingUrl}` : undefined}
                                    />
                                    <InfoRow
                                        icon={icMail}
                                        iconAlt="HR email"
                                        label="HR email"
                                        value={company.hrEmail}
                                        href={company.hrEmail ? `mailto:${company.hrEmail}` : undefined}
                                    />
                                    <InfoRow icon={icPhone} iconAlt="HR phone" label="HR phone" value={company.hrPhone} href={company.hrPhone ? `tel:${company.hrPhone}` : undefined} external={false} />
                                </CompanyDetailSection>
                            )}

                            {(company.openedStudent || company.openedWorldTraveler || company.openedEnglishSpeaking) && (
                                <CompanyDetailSection title="Job preferences" number={2}>
                                    {company.openedStudent && (
                                        <div className="company-detail-job-pref mb-3">
                                            <div className="company-detail-job-pref-title">Open for student jobs</div>
                                            <InfoRow icon={icArea} iconAlt="Student job Areas" label="Which Areas" value={cityLabel} />
                                            <InfoRow label="Which Corporate Areas" value={company.studentJobAreas} />
                                        </div>
                                    )}
                                    {company.openedWorldTraveler && (
                                        <div className="company-detail-job-pref mb-3">
                                            <div className="company-detail-job-pref-title">Open for World Traveler</div>
                                            <InfoRow icon={icArea} iconAlt="World Traveler Areas" label="Which Areas" value={cityLabel} />
                                            <InfoRow label="Which Corporate Areas" value={company.worldTravelerAreas} />
                                        </div>
                                    )}
                                    {company.openedEnglishSpeaking && (
                                        <div className="company-detail-job-pref mb-3">
                                            <div className="company-detail-job-pref-title">Open for English Speaking Only</div>
                                            <InfoRow icon={icArea} iconAlt="English Speaking Areas" label="Which Areas" value={cityLabel} />
                                            <InfoRow label="Which Corporate Areas" value={company.enSpeakingAreas} />
                                        </div>
                                    )}
                                </CompanyDetailSection>
                            )}

                            {studyFields.length > 0 && (
                                <CompanyDetailSection title="Areas & directions" number={3}>
                                    {studyFields.map((sf, idx) => (
                                        <div key={idx} className="company-detail-study-field mb-3">
                                            <InfoRow label="Study Field" value={sf.studyField} />
                                            <InfoRow label="Preferred Directions" value={sf.preferredStudyDirections} />
                                            <InfoRow label="Specialist Areas" value={sf.specialistAreas} />
                                        </div>
                                    ))}
                                </CompanyDetailSection>
                            )}
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCompanyHiringView;
